import { FONT_REGISTRY } from './font-registry';
import type { FontKey } from './types';

/** The framework default — already loaded by the AK shell, no Google Fonts request. */
const DEFAULT_FONT_KEY: FontKey = 'atlassian-sans';

/**
 * Coerce an arbitrary backend string into a known `FontKey`.
 *
 * The backend stores typography as free-form `headingFont` / `bodyFont`
 * strings. A value the FE doesn't recognise — a font added server-side before
 * this enum is updated, a data migration, or a third-party writer — must NOT
 * reach the `FONT_REGISTRY[key]` lookups in `buildGoogleFontsUrl` and
 * `HubTypographyScope`: those dereference the entry directly, so an unknown key
 * would throw on `undefined` and take down the whole hub render.
 *
 * Returns the validated key, or `'atlassian-sans'` for `null` / `undefined` /
 * any unrecognised value. A plain `as FontKey` cast wouldn't be safe here —
 * the `??` fallback only covers nullish, not invalid-but-non-null strings.
 *
 * Uses an own-property check rather than `in` / bracket-access: both of those
 * traverse the prototype chain, so a backend value of `'toString'` or
 * `'constructor'` would otherwise pass the guard and then resolve to an
 * inherited member (not a `FontEntry`) downstream.
 */
export const toFontKey = (value: string | null | undefined): FontKey =>
	value != null && Object.prototype.hasOwnProperty.call(FONT_REGISTRY, value)
		? (value as FontKey)
		: DEFAULT_FONT_KEY;
